import React, { useMemo, useState } from 'react';
import {
  Container, Typography, Card, CardContent, Box, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress, Collapse, IconButton,
  Alert, Snackbar, Stack, Tooltip, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Link,
} from '@mui/material';
import {
  KeyboardArrowDown, KeyboardArrowUp, Download as DownloadIcon, ChevronLeft, ChevronRight, Settings as SettingsIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment-timezone';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function fmt(seconds: number | undefined) {
  const s = Math.trunc(seconds || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h${String(m).padStart(2, '0')}m`;
}

function apiErrorMessage(error: unknown): string {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message || err?.message || 'Request failed';
}

type PayrollAction = 'supervisor' | 'payroll' | 'lock' | 'unlock' | 'reopen';

const PayrollReviewPage: React.FC = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  // Payroll always works on whole pay periods (the server refuses any other range).
  const periodsQuery = useQuery({
    queryKey: ['payroll-periods'],
    queryFn: async () => (await apiClient.payroll.periods()).data?.data,
  });
  const periodList: Array<{ startDate: string; endDate: string; key: string }> = periodsQuery.data?.periods || [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const currentKey: string | undefined = periodsQuery.data?.current?.key;
  const activeKey = selectedKey || currentKey || '';
  const activePeriod = periodList.find((p) => p.key === activeKey);
  const periodStart = activePeriod?.startDate || '';
  const periodEnd = activePeriod?.endDate || '';
  const activeIndex = periodList.findIndex((p) => p.key === activeKey);
  const [bulkConfirm, setBulkConfirm] = useState<null | 'payroll' | 'lock' | 'close' | 'reopenPayroll'>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ type: 'biweekly', biweeklyAnchorDate: '', timezone: 'America/Monterrey' });
  const isAdmin = ['administrator', 'super_user'].includes(String((user as any)?.role || ''));
  const [openId, setOpenId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['payroll-review', periodStart, periodEnd],
    enabled: Boolean(periodStart && periodEnd),
    queryFn: async () => {
      const res = await apiClient.payroll.review({ periodStart, periodEnd });
      return res.data;
    },
  });

  const bulk = useMutation({
    mutationFn: async (action: 'payroll' | 'lock') =>
      (await apiClient.payroll.bulk(action, { periodStart, periodEnd })).data?.data,
    onSuccess: (data: any) => {
      const skipped = (data?.skipped || []) as Array<{ reason: string }>;
      const reasons = Array.from(new Set(skipped.map((s) => s.reason))).join(', ');
      setToast({
        severity: skipped.length ? 'error' : 'success',
        message: `${data?.action === 'lock' ? 'Locked' : 'Approved for payroll'}: ${data?.done?.length || 0}` +
          (skipped.length ? ` · Skipped ${skipped.length} (${reasons})` : ''),
      });
      qc.invalidateQueries({ queryKey: ['payroll-review'] });
    },
    onError: (error) => setToast({ severity: 'error', message: apiErrorMessage(error) }),
    onSettled: () => setBulkConfirm(null),
  });

  const periodGate = useMutation({
    mutationFn: async (action: 'close' | 'reopenPayroll') => {
      const body = { periodStart, periodEnd };
      if (action === 'close') return apiClient.payroll.closePeriod(body);
      return apiClient.payroll.reopenPeriod(body);
    },
    onSuccess: (_data, action) => {
      setToast({
        severity: 'success',
        message: action === 'close'
          ? 'Payroll closed. Times stay locked until the payroll is reopened.'
          : 'Payroll reopened. Unlock an employee to change their times.',
      });
      qc.invalidateQueries({ queryKey: ['payroll-review'] });
    },
    onError: (error) => setToast({ severity: 'error', message: apiErrorMessage(error) }),
    onSettled: () => setBulkConfirm(null),
  });

  const savePayPeriod = useMutation({
    mutationFn: async () => apiClient.payroll.savePayPeriod(settingsForm),
    onSuccess: () => {
      setToast({ severity: 'success', message: 'Pay period saved' });
      setSettingsOpen(false);
      setSelectedKey(null);
      qc.invalidateQueries({ queryKey: ['payroll-periods'] });
    },
    onError: (error) => setToast({ severity: 'error', message: apiErrorMessage(error) }),
  });

  const rows = query.data?.data?.rows || [];
  const periodClosed = Boolean(query.data?.data?.periodClosed);
  const canStillLock = rows.some((row: any) => String(row.approvalStatus || '') === 'PAYROLL_APPROVED');
  const canApprove = (user?.permissions || []).includes('payroll.approve.supervisor');
  const canPayroll = (user?.permissions || []).includes('payroll.approve.payroll');
  const canLock = (user?.permissions || []).includes('payroll.lock');
  const canReopen = (user?.permissions || []).includes('payroll.reopen');
  // Only super users and the two time-off approvers may unapprove (the server enforces the same rule).
  const canUnapprove = Boolean(query.data?.data?.canUnapprove) && (canLock || canReopen);

  const mutate = useMutation({
    mutationFn: async ({
      employeeId,
      action,
      start,
      end,
    }: {
      employeeId: string;
      action: PayrollAction;
      start: string;
      end: string;
    }) => {
      const body = { periodStart: start, periodEnd: end };
      if (action === 'supervisor') return apiClient.payroll.approveSupervisor(employeeId, body);
      if (action === 'payroll') return apiClient.payroll.approvePayroll(employeeId, body);
      if (action === 'lock') return apiClient.payroll.lock(employeeId, body);
      if (action === 'unlock') return apiClient.payroll.unlock(employeeId, { ...body, reason: 'Unlocked to make changes' });
      return apiClient.payroll.reopen(employeeId, { ...body, reason: 'Authorized reopen' });
    },
    onMutate: (vars) => {
      setPendingKey(`${vars.employeeId}:${vars.action}`);
    },
    onSuccess: (_data, vars) => {
      const labels: Record<PayrollAction, string> = {
        supervisor: 'Supervisor approved',
        payroll: 'Payroll approved',
        lock: 'Period locked',
        unlock: 'Employee unlocked. You can change their times.',
        reopen: 'Unlocked and unapproved. The employee is open again.',
      };
      setToast({ severity: 'success', message: labels[vars.action] });
      qc.invalidateQueries({ queryKey: ['payroll-review'] });
    },
    onError: (error) => {
      setToast({ severity: 'error', message: apiErrorMessage(error) });
    },
    onSettled: () => {
      setPendingKey(null);
    },
  });

  const runAction = (row: any, action: PayrollAction) => {
    const start = row.payPeriod?.start || periodStart;
    const end = row.payPeriod?.end || periodEnd;
    if (!row.employeeId) {
      setToast({ severity: 'error', message: 'Missing employee id for this row.' });
      return;
    }
    mutate.mutate({ employeeId: row.employeeId, action, start, end });
  };

  const totals = useMemo(() => {
    return rows.reduce((acc: any, r: any) => {
      acc.payable += r.payableSeconds || 0;
      acc.exceptions += r.exceptionCount || 0;
      if (['SUPERVISOR_APPROVED', 'PAYROLL_APPROVED', 'LOCKED'].includes(String(r.approvalStatus))) acc.reviewed += 1;
      if (r.changedAfterApproval) acc.changed += 1;
      return acc;
    }, { payable: 0, exceptions: 0, reviewed: 0, changed: 0 });
  }, [rows]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>Payroll Review</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Canonical payable hours from attendance punches (integer seconds). Monitoring idle, screen lock, and call recording never change pay.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Pay period flow: supervisors review their teams (optional, target <strong>Wednesday 12:00</strong>) · payroll reviews
        with accounting Tue/Wed · <strong>Approve all READY for payroll</strong> Thursday when payroll goes to accounting ·
        late changes (Friday absences, tardies) stay allowed and show as <strong>Changed after approval</strong> — re-approve
        to accept · <strong>Lock all</strong> Friday 12:00. A locked row says <strong>Unlock</strong> so times can still
        change. Unlock has to happen before payroll is closed. After that, times stay locked until the payroll is reopened.
      </Alert>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <IconButton
            aria-label="Previous pay period"
            disabled={activeIndex <= 0}
            onClick={() => setSelectedKey(periodList[activeIndex - 1]?.key || null)}
          >
            <ChevronLeft />
          </IconButton>
          <TextField
            select
            label="Pay period"
            size="small"
            sx={{ minWidth: 300 }}
            value={activePeriod ? activeKey : ''}
            onChange={(e) => setSelectedKey(e.target.value)}
          >
            {periodList.map((p) => (
              <MenuItem key={p.key} value={p.key}>
                {moment(p.startDate).format('ddd DD MMM')} → {moment(p.endDate).format('ddd DD MMM YYYY')}
                {p.key === currentKey ? ' (current)' : ''}
              </MenuItem>
            ))}
          </TextField>
          <IconButton
            aria-label="Next pay period"
            disabled={activeIndex < 0 || activeIndex >= periodList.length - 1}
            onClick={() => setSelectedKey(periodList[activeIndex + 1]?.key || null)}
          >
            <ChevronRight />
          </IconButton>
          <Button variant="contained" onClick={() => query.refetch()}>Refresh</Button>
          {canPayroll && (
            <Button variant="outlined" disabled={!rows.length || bulk.isPending} onClick={() => setBulkConfirm('payroll')}>
              Approve all READY for payroll
            </Button>
          )}
          {canLock && (
            <Tooltip title={canStillLock ? 'Lock every employee already approved for payroll' : 'Already locked. Use Unlock on a row to change times.'}>
              <span>
                <Button variant="outlined" color="warning" disabled={!canStillLock || bulk.isPending || periodClosed} onClick={() => setBulkConfirm('lock')}>
                  Lock all
                </Button>
              </span>
            </Tooltip>
          )}
          {canLock && !periodClosed && rows.some((row: any) => String(row.approvalStatus) === 'LOCKED') && (
            <Button variant="outlined" disabled={periodGate.isPending} onClick={() => setBulkConfirm('close')}>
              Close payroll
            </Button>
          )}
          {canUnapprove && periodClosed && (
            <Button variant="outlined" color="warning" disabled={periodGate.isPending} onClick={() => setBulkConfirm('reopenPayroll')}>
              Reopen payroll
            </Button>
          )}
          {periodClosed && <Chip color="warning" label="Payroll closed" />}
          {isAdmin && (
            <IconButton aria-label="Pay period settings" onClick={() => {
              const pp = periodsQuery.data?.payPeriod || {};
              setSettingsForm({
                type: pp.type || 'biweekly',
                biweeklyAnchorDate: pp.biweeklyAnchorDate || '',
                timezone: periodsQuery.data?.timezone || 'America/Monterrey',
              });
              setSettingsOpen(true);
            }}>
              <SettingsIcon />
            </IconButton>
          )}
          <Button startIcon={<DownloadIcon />} onClick={() => apiClient.payroll.exportCsv({ periodStart, periodEnd }).then((r) => {
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `payroll-${periodStart}-${periodEnd}.csv`;
            a.click();
          })}>Export CSV</Button>
          <Chip label={`Payable ${fmt(totals.payable)}`} />
          <Chip color={totals.exceptions ? 'warning' : 'success'} label={`${totals.exceptions} exceptions`} />
          <Chip
            variant="outlined"
            label={`Reviewed by supervisors ${totals.reviewed}/${rows.length} · target Wed 12:00 (optional)`}
          />
          {totals.changed > 0 && <Chip color="warning" label={`${totals.changed} changed after approval`} />}
        </CardContent>
      </Card>
      {query.isLoading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Employee</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Raw</TableCell>
                <TableCell>Regular</TableCell>
                <TableCell>OT</TableCell>
                <TableCell>Paid break</TableCell>
                <TableCell>Unpaid</TableCell>
                <TableCell>Adj</TableCell>
                <TableCell>Payable</TableCell>
                <TableCell>Exceptions</TableCell>
                <TableCell>Approval</TableCell>
                <TableCell>Ready</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: any) => {
                const status = String(row.approvalStatus || 'OPEN');
                const ready = Boolean(row.payrollReady);
                const locked = status === 'LOCKED';
                const supervisorDone = status === 'SUPERVISOR_APPROVED' || status === 'PAYROLL_APPROVED' || locked;
                const payrollDone = status === 'PAYROLL_APPROVED' || locked;
                const busy = Boolean(pendingKey?.startsWith(`${row.employeeId}:`));

                const changed = Boolean(row.changedAfterApproval);
                // Supervisor is optional and never undoes a payroll approval; re-approving accepts later changes.
                const supervisorEnabled = canApprove && !locked && status !== 'PAYROLL_APPROVED' && (!supervisorDone || changed);
                const payrollEnabled = canPayroll && !locked && ready && (!payrollDone || changed);
                const lockEnabled = canLock && status === 'PAYROLL_APPROVED' && !periodClosed;
                const unlockEnabled = canUnapprove && locked && !periodClosed;

                return (
                  <React.Fragment key={row.employeeId}>
                    <TableRow>
                      <TableCell>
                        <IconButton size="small" onClick={() => setOpenId(openId === row.employeeId ? null : row.employeeId)}>
                          {openId === row.employeeId ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{row.employeeName || row.employeeId}</TableCell>
                      <TableCell>{row.payPeriod?.start} → {row.payPeriod?.end}</TableCell>
                      <TableCell>{fmt(row.rawWorkedSeconds)}</TableCell>
                      <TableCell>{fmt(row.regularSeconds)}</TableCell>
                      <TableCell>{fmt(row.overtimeSeconds)}</TableCell>
                      <TableCell>{fmt(row.paidBreakSeconds)}</TableCell>
                      <TableCell>{fmt(row.unpaidSeconds)}</TableCell>
                      <TableCell>{fmt(row.adjustmentSeconds)}</TableCell>
                      <TableCell><strong>{fmt(row.payableSeconds)}</strong></TableCell>
                      <TableCell>{row.exceptionCount}</TableCell>
                      <TableCell>
                        {locked ? <Chip size="small" color="success" label="Approved" /> : status}
                        {changed && (
                          <Tooltip
                            title={(row.approvalChanges || [])
                              .map((c: any) => `${c.field}: ${c.field.endsWith('Seconds') ? `${fmt(c.before)} → ${fmt(c.after)}` : `${c.before} → ${c.after}`}`)
                              .join(' · ')}
                          >
                            <Chip size="small" color="warning" label="Changed after approval" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={ready ? 'success' : 'error'} label={ready ? 'READY' : 'NOT READY'} />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25} alignItems="flex-start">
                          {canApprove && (
                            <Tooltip title={periodClosed && locked ? 'Payroll is closed. Reopen the payroll to change these times.' : locked ? 'Locked — unlock first' : status === 'PAYROLL_APPROVED' ? 'Already approved for payroll' : supervisorDone && !changed ? 'Already supervisor-approved' : 'Mark supervisor approved (optional)'}>
                              <span>
                                <Button
                                  type="button"
                                  size="small"
                                  disabled={!supervisorEnabled || busy}
                                  onClick={() => runAction(row, 'supervisor')}
                                >
                                  {pendingKey === `${row.employeeId}:supervisor` ? 'Working…' : 'Supervisor'}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {canPayroll && (
                            <Tooltip
                              title={
                                !ready
                                  ? 'Resolve blocking exceptions (NOT READY)'
                                  : locked
                                    ? (periodClosed ? 'Payroll is closed. Reopen the payroll to change these times.' : 'Locked — unlock first')
                                  : payrollDone && !changed
                                    ? 'Already payroll-approved'
                                    : changed
                                      ? 'Accept the changes made after approval'
                                      : 'Mark payroll approved'
                              }
                            >
                              <span>
                                <Button
                                  type="button"
                                  size="small"
                                  disabled={!payrollEnabled || busy}
                                  onClick={() => runAction(row, 'payroll')}
                                >
                                  {pendingKey === `${row.employeeId}:payroll` ? 'Working…' : 'Payroll'}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {canUnapprove && locked && (
                            <Tooltip title={periodClosed ? 'Payroll is closed. Reopen the payroll first.' : 'Unlock and unapprove: return this employee to open so times can change'}>
                              <span>
                                <Button
                                  type="button"
                                  size="small"
                                  color="warning"
                                  disabled={!unlockEnabled || busy}
                                  onClick={() => runAction(row, 'reopen')}
                                >
                                  {pendingKey === `${row.employeeId}:reopen` ? 'Working…' : 'Unlock'}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {canLock && !locked && (
                            <Tooltip title={periodClosed ? 'Payroll is closed' : status !== 'PAYROLL_APPROVED' ? 'Payroll approval required first' : 'Lock period for export'}>
                              <span>
                                <Button
                                  type="button"
                                  size="small"
                                  disabled={!lockEnabled || busy}
                                  onClick={() => runAction(row, 'lock')}
                                >
                                  {pendingKey === `${row.employeeId}:lock` ? 'Working…' : 'Lock'}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={14} sx={{ py: 0 }}>
                        <Collapse in={openId === row.employeeId} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2 }}>
                            {(row.days || []).map((d: any) => (
                              <Box key={d.workDate} sx={{ mb: 1 }}>
                                <Typography variant="subtitle2">
                                  <Link
                                    component={RouterLink}
                                    to={`/management/time-records?userId=${encodeURIComponent(row.employeeId)}&date=${encodeURIComponent(d.workDate)}`}
                                    underline="hover"
                                    title="Open this employee's time records for this date"
                                  >
                                    {d.workDate}
                                  </Link>
                                  {' — payable '}{fmt(d.payableSeconds)}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  In {d.clockInAt || '—'} · Lunch {d.lunchStartAt || '—'} / {d.lunchEndAt || '—'} · Out {d.clockOutAt || '—'}
                                </Typography>
                                {(d.exceptions || []).map((ex: any, i: number) => (
                                  <Chip key={i} size="small" color="warning" label={`${ex.code}: ${ex.message}`} sx={{ mr: 0.5, mt: 0.5 }} />
                                ))}
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={Boolean(bulkConfirm)} onClose={() => setBulkConfirm(null)}>
        <DialogTitle>
          {bulkConfirm === 'lock' && 'Lock all approved employees?'}
          {bulkConfirm === 'close' && 'Close payroll?'}
          {bulkConfirm === 'reopenPayroll' && 'Reopen payroll?'}
          {bulkConfirm === 'payroll' && 'Approve all READY employees for payroll?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Pay period {periodStart} → {periodEnd}.{' '}
            {bulkConfirm === 'lock' && 'After lock, each row says Unlock so times can still be changed. Do that before you close payroll.'}
            {bulkConfirm === 'close' && 'This is the final close. Locked times stay locked. To change them after this, reopen the payroll, then unlock the employee.'}
            {bulkConfirm === 'reopenPayroll' && 'Reopening lets you unlock an employee and change their times. It does not unlock anyone by itself.'}
            {bulkConfirm === 'payroll' && 'NOT READY employees are skipped. Later changes stay allowed and show as “Changed after approval”.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={bulkConfirm === 'lock' || bulkConfirm === 'close' || bulkConfirm === 'reopenPayroll' ? 'warning' : 'primary'}
            disabled={bulk.isPending || periodGate.isPending}
            onClick={() => {
              if (bulkConfirm === 'payroll' || bulkConfirm === 'lock') bulk.mutate(bulkConfirm);
              if (bulkConfirm === 'close' || bulkConfirm === 'reopenPayroll') periodGate.mutate(bulkConfirm);
            }}
          >
            {(bulk.isPending || periodGate.isPending) ? 'Working…' : bulkConfirm === 'lock' ? 'Lock all' : bulkConfirm === 'close' ? 'Close payroll' : bulkConfirm === 'reopenPayroll' ? 'Reopen payroll' : 'Approve all'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <DialogTitle>Pay period</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important', minWidth: 360 }}>
          <TextField select label="Frequency" value={settingsForm.type} onChange={(e) => setSettingsForm({ ...settingsForm, type: e.target.value })}>
            <MenuItem value="biweekly">Every 14 days (catorcena)</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="semimonthly">Semimonthly (1-15, 16-end)</MenuItem>
          </TextField>
          {settingsForm.type === 'biweekly' && (
            <TextField
              type="date"
              label="First day of a pay period"
              helperText="Any period start, e.g. 2026-09-26 for the catorcena paid Friday 2026-10-09"
              InputLabelProps={{ shrink: true }}
              value={settingsForm.biweeklyAnchorDate}
              onChange={(e) => setSettingsForm({ ...settingsForm, biweeklyAnchorDate: e.target.value })}
            />
          )}
          <TextField label="Timezone" value={settingsForm.timezone} onChange={(e) => setSettingsForm({ ...settingsForm, timezone: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={savePayPeriod.isPending} onClick={() => savePayPeriod.mutate()}>Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled" sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Container>
  );
};

export default PayrollReviewPage;
